import { Box, IconButton } from '@chakra-ui/react';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, Editor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect } from 'react';

import { Icon } from 'packages/common-frontend/components/icons';

import './richTextEditor.scss';

const Toolbar = ({ editor }: { editor: Editor }) => {
  return (
    <Box className="fixed-menu">
      <IconButton
        icon={<Icon icon="b" />}
        boxSize={6}
        aria-label="bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="italic" />}
        boxSize={6}
        aria-label="italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="strikethrough" />}
        boxSize={6}
        aria-label="strikethrough"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="code" />}
        boxSize={6}
        aria-label="code"
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editor.can().chain().focus().toggleCode().run()}
        className={editor.isActive('code') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="list" />}
        boxSize={6}
        aria-label="list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="list-ol" />}
        boxSize={6}
        aria-label="list-ol"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="rectangle-code" />}
        boxSize={6}
        aria-label="rectangle-code"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="block-quote" />}
        boxSize={6}
        aria-label="block-quote"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
      />
      <IconButton
        icon={<Icon icon="horizontal-rule" />}
        boxSize={6}
        aria-label="horizontal-rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      />
      <IconButton
        icon={<Icon icon="arrow-rotate-left" />}
        boxSize={6}
        aria-label="undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
      />
      <IconButton
        icon={<Icon icon="arrow-rotate-right" />}
        boxSize={6}
        aria-label="redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
      />
    </Box>
  );
};

export const RichTextEditor = ({
  text,
  setText,
  isEditable,
  placeHolderText,
}: {
  text: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  isEditable: boolean;
  placeHolderText?: string;
}) => {
  const editor = useEditor({
    editable: isEditable,
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: placeHolderText ?? '',
      }),
    ],
    content: text,

    onUpdate: ({ editor: newEditor }) => {
      if (newEditor.isEmpty) {
        // makes it easy to check if text is empty vs checking for empty html tags
        setText('');
      } else {
        setText(newEditor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (text.length === 0 && editor) {
      editor.commands.setContent('');
    }
  }, [editor, text.length]);

  return (
    <Box>
      <Box>{editor && <Toolbar editor={editor} />}</Box>
      <Box
        borderWidth="1px"
        padding="8px"
        minHeight="50px"
        borderBottomLeftRadius="6px"
        borderBottomRightRadius="6px"
        borderTopWidth={0}
        bgColor="white"
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export const RenderStaticHtml = ({
  textHtml,
  color = 'inherit',
}: {
  textHtml: string;
  color?: string;
}) => {
  const editor = useEditor({
    editable: false,
    extensions: [StarterKit],
    content: textHtml,
  });

  return <EditorContent editor={editor} style={{ color }} />;
};
